"use client";

import { ReactNode, useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  Zap,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Menu,
  X,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import CookieConsent from "@/components/ui/CookieConsent";
import { useAuth } from "@/lib/useAuth";

export default function PublicLayout({ children }: { children: ReactNode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const { user, isLoading, logout } = useAuth();

  // Capture referral code on any public page with ?ref=CODE
  useEffect(() => {
    const captureReferralCode = async () => {
      const refCode = new URLSearchParams(window.location.search).get("ref");
      if (!refCode) return;

      try {
        const { captureReferralCode: persistCode } = await import("@/lib/referral-persistence");
        const { storeTrackingParams } = await import("@/lib/referral-tracking");

        // Store in localStorage and sessionStorage
        persistCode(refCode);
        storeTrackingParams({ referralCode: refCode });

        console.log("Referral code captured on public page:", refCode);
      } catch (err) {
        console.error("Failed to capture referral code:", err);
      }
    };

    captureReferralCode();
  }, []);

  const getDashboardLink = () => {
    if (!user) return "/";
    if (user.role === "ADMIN" || user.role === "SUPER_ADMIN") return "/admin";
    if (user.hasReferral) return "/referrals";
    if (user.role === "GARAGE_STAFF") return "/garage/jobs";
    if (user.hasGarage) return "/garage/dashboard";
    return "/";
  };

  const handleLogout = async () => {
    await logout();
    router.push("/");
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/features", label: "Features" },
    { href: "/pricing", label: "Pricing" },
    { href: "/find-garage", label: "Find Garage" },
    { href: "/join-referrals", label: "Referrals", badge: "20%" },
    { href: "/blog", label: "Blog" },
    { href: "/contact", label: "Contact" },
  ];

  const isCurrent = (href: string) => {
    if (href === "/" && pathname === "/") return true;
    if (href !== "/" && pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900 selection:bg-blue-500 selection:text-white">
      <CookieConsent />

      {/* ── 1. MODERN GLASSMORPHIC HEADER ──────────────────────────────────── */}
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-50 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0 group">
            <Image
              src="/logo.png"
              alt="Garage Boost"
              width={180}
              height={52}
              className="h-9 sm:h-10 w-auto object-contain transition-transform group-hover:scale-[1.02]"
              priority
            />
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1.5 xl:gap-2">
            {navLinks.map((link) => {
              const active = isCurrent(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`px-3 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 ${
                    active
                      ? "text-blue-600 bg-blue-50/80 font-extrabold"
                      : "text-slate-600 hover:text-slate-950 hover:bg-slate-100/70"
                  }`}
                >
                  <span>{link.label}</span>
                  {link.badge && (
                    <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200">
                      {link.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Auth Actions */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {isLoading ? (
              <div className="w-24 h-10 bg-slate-200 rounded-xl animate-pulse" />
            ) : user ? (
              <>
                <Link
                  href={getDashboardLink()}
                  className="text-sm font-bold text-slate-700 hover:text-blue-600 px-3.5 py-2 transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-xs sm:text-sm font-bold bg-slate-900 hover:bg-red-600 text-white px-4 py-2.5 rounded-xl shadow-xs transition-all cursor-pointer"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="text-sm font-bold text-slate-700 hover:text-blue-600 px-3.5 py-2 transition-colors"
                >
                  Log In
                </Link>
                <Link
                  href="/signup"
                  className="group text-xs sm:text-sm font-bold bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white px-5 py-2.5 rounded-xl shadow-md shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-[1.02] transition-all flex items-center gap-1.5"
                >
                  <span>Start 14 Days Free</span>
                  <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger Button */}
          <button
            type="button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 text-slate-700 hover:text-blue-600 hover:border-blue-200 transition-colors cursor-pointer"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white shadow-xl animate-in slide-in-from-top-2 duration-200">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => {
                const active = isCurrent(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between ${
                      active
                        ? "text-blue-600 bg-blue-50"
                        : "text-slate-700 hover:text-blue-600 hover:bg-slate-50"
                    }`}
                  >
                    <span>{link.label}</span>
                    {link.badge && (
                      <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                );
              })}

              {/* Mobile Auth Drawer Buttons */}
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col gap-2">
                {isLoading ? (
                  <div className="w-full h-10 bg-slate-200 rounded-xl animate-pulse" />
                ) : user ? (
                  <>
                    <Link
                      href={getDashboardLink()}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full text-center text-sm font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl py-3 transition-all"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-center text-sm font-bold bg-slate-900 text-white rounded-xl py-3 shadow-md transition-all cursor-pointer"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full text-center text-sm font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl py-3 transition-all"
                    >
                      Log In
                    </Link>
                    <Link
                      href="/signup"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full text-center text-sm font-bold bg-linear-to-r from-blue-600 to-teal-600 text-white rounded-xl py-3.5 shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2"
                    >
                      <span>Start 14 Days Free</span>
                      <ArrowRight size={16} />
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Page Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* ── 2. NEXT-GEN SIGNATURE UK GARAGE FOOTER ──────────────────────────── */}
      <footer className="bg-[#070b14] text-white relative overflow-hidden border-t border-slate-800/80">
        {/* Ambient Top Gradient Accent Line */}
        <div className="h-[2px] w-full bg-linear-to-r from-transparent via-blue-500 via-teal-400 to-transparent shadow-xs shadow-blue-500/50" />

        {/* Ambient Background Radial Glows */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* ── Main Footer Columns ── */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
            
            {/* Col 1: Brand & Identity (4 Cols) */}
            <div className="lg:col-span-4 space-y-4">
              <Link href="/" className="inline-block group">
                <Image
                  src="/logo-white.png"
                  alt="Garage Boost"
                  width={190}
                  height={56}
                  className="h-10 sm:h-11 w-auto object-contain transition-transform group-hover:scale-[1.02]"
                />
              </Link>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-sm">
                The all-in-one automated booking intake, DVSA MOT lookup, and workshop management software built specifically for independent UK garages and service stations.
              </p>

              <div className="pt-2 flex items-center gap-3">
                <span className="text-xs font-semibold text-slate-400">Connect:</span>
                <a
                  href="https://www.facebook.com/garage.bizzboost.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GarageBoost on Facebook"
                  className="w-8 h-8 bg-slate-900 hover:bg-blue-600 border border-slate-800 rounded-xl flex items-center justify-center text-slate-300 hover:text-white transition-all shadow-xs"
                >
                  <Facebook size={14} />
                </a>
                <a
                  href="https://x.com/BizzBoostUK"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GarageBoost on Twitter"
                  className="w-8 h-8 bg-slate-900 hover:bg-cyan-500 border border-slate-800 rounded-xl flex items-center justify-center text-slate-300 hover:text-white transition-all shadow-xs"
                >
                  <Twitter size={14} />
                </a>
                <a
                  href="https://www.linkedin.com/company/bizz-boost/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GarageBoost on LinkedIn"
                  className="w-8 h-8 bg-slate-900 hover:bg-blue-700 border border-slate-800 rounded-xl flex items-center justify-center text-slate-300 hover:text-white transition-all shadow-xs"
                >
                  <Linkedin size={14} />
                </a>
              </div>
            </div>

            {/* Col 2: Supported Platforms (3 Cols) */}
            <div className="lg:col-span-3 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-200 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Supported Platforms
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
                <li><Link href="/find-garage" className="hover:text-teal-300 transition-colors font-semibold text-teal-400">🔍 Find a Local Garage</Link></li>
                <li><Link href="/integrations/bookmygarage" className="hover:text-teal-300 transition-colors">BookMyGarage Automation</Link></li>
                <li><Link href="/integrations/clickmechanic" className="hover:text-teal-300 transition-colors">ClickMechanic Automation</Link></li>
                <li><Link href="/integrations/fixter" className="hover:text-teal-300 transition-colors">Fixter Automation</Link></li>
                <li><Link href="/integrations/whocanfixmycar" className="hover:text-teal-300 transition-colors">WhoCanFixMyCar Automation</Link></li>
                <li><Link href="/pricing" className="hover:text-teal-300 transition-colors">Pricing & Plans</Link></li>
              </ul>
            </div>

            {/* Col 3: Comparisons (2.5 Cols) */}
            <div className="lg:col-span-2 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-200 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span> Compare
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-400">
                <li><Link href="/compare/garageboost-vs-techman" className="hover:text-teal-300 transition-colors">vs TechMan</Link></li>
                <li><Link href="/compare/garageboost-vs-garage-hive" className="hover:text-teal-300 transition-colors">vs Garage Hive</Link></li>
                <li><Link href="/compare/garageboost-vs-autochain" className="hover:text-teal-300 transition-colors">vs AutoChain</Link></li>
                <li><Link href="/compare/garageboost-vs-dragon2000" className="hover:text-teal-300 transition-colors">vs Dragon2000</Link></li>
                <li><Link href="/blog" className="hover:text-teal-300 transition-colors">Growth & Tech Blog</Link></li>
              </ul>
            </div>

            {/* Col 4: Helpdesk (2.5 Cols) */}
            <div className="lg:col-span-3 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-200 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span> UK Helpdesk
              </h3>
              <div className="space-y-3 bg-slate-900/70 border border-slate-800/90 rounded-2xl p-4 text-xs text-slate-300">
                <div className="flex items-center gap-2.5">
                  <Mail size={13} className="text-blue-400 shrink-0" />
                  <a href="mailto:cs@bizzboost.uk" className="text-white font-bold hover:text-teal-300 transition-colors truncate">
                    cs@bizzboost.uk
                  </a>
                </div>
                <div className="flex items-center gap-2.5">
                  <Phone size={13} className="text-teal-400 shrink-0" />
                  <a href="tel:07380448187" className="text-white font-bold hover:text-teal-300 transition-colors">
                    07380 448187
                  </a>
                </div>
                <div className="flex items-start gap-2.5 pt-1 border-t border-slate-800/80">
                  <MapPin size={13} className="text-slate-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Great Portland St, London</p>
                    <p className="text-[11px] text-slate-400">W1W 5PF, United Kingdom</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legal & Trademark Disclaimer Bar */}
          <div className="pt-8 border-t border-slate-800/60 text-center">
            <p className="text-[11px] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              <strong>Disclaimer & Trademark Notice:</strong> Garage Boost is an independent email booking automation software for UK garages. BookMyGarage, ClickMechanic, Fixter, and WhoCanFixMyCar are registered trademarks of their respective owners. Garage Boost is not affiliated with, endorsed by, or partnered with these companies.
            </p>
          </div>

          {/* Faint Giant Background Watermark */}
          <div className="pt-6 pb-4 text-center select-none pointer-events-none opacity-5">
            <span className="text-6xl sm:text-8xl md:text-9xl font-black tracking-widest text-white uppercase block leading-none">
              GARAGE BOOST
            </span>
          </div>

          {/* Bottom Copyright bar */}
          <div className="pt-6 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <p suppressHydrationWarning>
              © {new Date().getFullYear()} Garage Boost UK. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link href="/privacy" className="hover:text-slate-300 transition-colors">Privacy</Link>
              <Link href="/terms" className="hover:text-slate-300 transition-colors">Terms</Link>
              <Link href="/cookies" className="hover:text-slate-300 transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
